import React, { useState, useEffect } from 'react';
import L from 'leaflet';

// Import modular components
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import MapView from './components/MapView';
import ListView from './components/ListView';
import ProfileView from './components/ProfileView';
import UserProfileView from './components/UserProfileView';
import MainApp from './components/MainApp';
import ChatWindow from './components/ChatWindow';

// Import data
import { interests } from './data/interests';
import { cities, cityCoordinates } from './data/cities';

// Import utilities
import { calculateDistance } from './utils/distance';
import { signupUser, loginUser, logoutUser, onAuthChange, getAllUsers } from './firebase/auth';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const SwitzerlandMeetupApp = () => {
  // View state
  const [currentView, setCurrentView] = useState('landing');
  const [mapView, setMapView] = useState('map');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showTestInfo, setShowTestInfo] = useState(true);
  
  // User state
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Chat state
  const [chatUser, setChatUser] = useState(null);
  const [showChat, setShowChat] = useState(false);
  
  // Filter state
  const [showOnlySharedInterests, setShowOnlySharedInterests] = useState(true);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthChange((user) => {
      if (user) {
        setCurrentUser(user);
        setCurrentView('map');
        // Load all users when logged in
        loadUsers();
        setLoading(false);
      } else {
        setCurrentUser(null);
        setCurrentView('landing');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Load all users from Firestore
  const loadUsers = async () => {
    const allUsers = await getAllUsers();
    setUsers(allUsers);
  };
  
  // Form states
  const [signupForm, setSignupForm] = useState({
    name: '',
    email: '',
    password: '',
    location: '',
    city: '',
    interests: [],
    genderPreference: 'all',
    socials: {
      instagram: '',
      linkedin: ''
    }
  });

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  // Profile edit state
  const [profileEdit, setProfileEdit] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});

  // Handlers
  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const userData = {
      name: signupForm.name,
      location: signupForm.city,
      coordinates: cityCoordinates[signupForm.city] || cityCoordinates['Zurich'],
      interests: signupForm.interests,
      genderPreference: signupForm.genderPreference,
      avatar: 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=150&h=150&fit=crop&crop=face',
      bio: 'New to the platform!',
      gender: 'not-specified', // You can add gender field to signup form later
      socials: signupForm.socials
    };
    
    const result = await signupUser(signupForm.email, signupForm.password, userData);
    
    if (result.success) {
      setShowTestInfo(false);
      setCurrentUser(result.userData);
      setCurrentView('map');
      loadUsers();
      setLoading(false);
    } else {
      alert(`Signup failed: ${result.error}`);
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await loginUser(loginForm.email, loginForm.password);
    
    if (result.success) {
      setShowTestInfo(false);
      setCurrentUser(result.userData);
      setCurrentView('map');
      loadUsers();
      setLoading(false);
    } else {
      alert(`Login failed: ${result.error}`);
      setLoading(false);
    }
  };

  const quickLogin = async (email, password) => {
    setLoginForm({ email, password });
    setLoading(true);
    
    const result = await loginUser(email, password);
    
    if (result.success) {
      setShowTestInfo(false);
      setCurrentUser(result.userData);
      setCurrentView('map');
      loadUsers();
      setLoading(false);
    } else {
      alert(`Login failed: ${result.error}`);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logoutUser();
    setCurrentUser(null);
    setCurrentView('landing');
    setShowMobileMenu(false);
  };

  const toggleInterest = (interest) => {
    setSignupForm(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const toggleEditInterest = (interest) => {
    const newInterests = editedProfile.interests?.includes(interest)
      ? editedProfile.interests.filter(i => i !== interest)
      : [...(editedProfile.interests || []), interest];
    setEditedProfile({...editedProfile, interests: newInterests});
  };

  const getMatchingUsers = () => {
    if (!currentUser || !currentUser.coordinates) return [];
    
    // Filter out current user
    let filteredUsers = users.filter(user => user.id !== currentUser.id);
    
    // Apply interest filter if enabled
    if (showOnlySharedInterests) {
      filteredUsers = filteredUsers.filter(user => 
        user.interests?.some(interest => currentUser.interests?.includes(interest))
      );
    }
    
    // Apply gender preference filter
    filteredUsers = filteredUsers.filter(user =>
      currentUser.genderPreference === 'all' || 
      user.genderPreference === 'all' ||
      (currentUser.genderPreference === 'women' && user.gender === 'woman') ||
      (currentUser.genderPreference === 'men' && user.gender === 'man')
    );

    const usersWithDistance = filteredUsers.map(user => ({
      ...user,
      distance: user.coordinates ? calculateDistance(
        currentUser.coordinates.lat, 
        currentUser.coordinates.lng,
        user.coordinates.lat, 
        user.coordinates.lng
      ) : Infinity
    }));

    return usersWithDistance.sort((a, b) => a.distance - b.distance);
  };

  const getClusteredUsers = () => {
    const matchingUsers = getMatchingUsers();
    
    const clusters = {
      nearby: [],
      close: [],
      distant: []
    };

    matchingUsers.forEach(user => {
      if (user.distance <= 10) {
        clusters.nearby.push(user);
      } else if (user.distance <= 30) {
        clusters.close.push(user);
      } else {
        clusters.distant.push(user);
      }
    });

    return clusters;
  };

  const handleConnectWithUser = (user) => {
    setSelectedUser(user);
    setCurrentView('userProfile');
  };

  const startEditProfile = () => {
    setEditedProfile({ ...currentUser });
    setProfileEdit(true);
  };

  const saveProfile = () => {
    setCurrentUser(editedProfile);
    setProfileEdit(false);
  };

  const handleSendMessage = (user) => {
    setChatUser(user);
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setChatUser(null);
  };

  // Main Render
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      {currentView === 'landing' && (
        <LandingPage
          signupForm={signupForm}
          setSignupForm={setSignupForm}
          handleSignup={handleSignup}
          toggleInterest={toggleInterest}
          setCurrentView={setCurrentView}
          showTestInfo={showTestInfo}
          setShowTestInfo={setShowTestInfo}
        />
      )}
      
      {currentView === 'login' && (
        <LoginPage
          loginForm={loginForm}
          setLoginForm={setLoginForm}
          handleLogin={handleLogin}
          quickLogin={quickLogin}
          setCurrentView={setCurrentView}
        />
      )}
      
      {currentView === 'userProfile' && (
        <UserProfileView
          selectedUser={selectedUser}
          currentUser={currentUser}
          setCurrentView={setCurrentView}
          onSendMessage={handleSendMessage}
        />
      )}
      
      {(currentView === 'map' || currentView === 'profile') && (
        <MainApp
          currentUser={currentUser}
          currentView={currentView}
          setCurrentView={setCurrentView}
          mapView={mapView}
          setMapView={setMapView}
          showMobileMenu={showMobileMenu}
          setShowMobileMenu={setShowMobileMenu}
          handleLogout={handleLogout}
        >
          {currentView === 'map' && mapView === 'map' && (
            <MapView
              matchingUsers={getMatchingUsers()}
              handleConnectWithUser={handleConnectWithUser}
              showOnlySharedInterests={showOnlySharedInterests}
              setShowOnlySharedInterests={setShowOnlySharedInterests}
              currentUser={currentUser}
            />
          )}
          
          {currentView === 'map' && mapView === 'list' && (
            <ListView
              clusters={getClusteredUsers()}
              currentUser={currentUser}
              handleConnectWithUser={handleConnectWithUser}
              showOnlySharedInterests={showOnlySharedInterests}
              setShowOnlySharedInterests={setShowOnlySharedInterests}
            />
          )}
          
          {currentView === 'profile' && (
            <ProfileView
              currentUser={currentUser}
              setCurrentUser={setCurrentUser}
              profileEdit={profileEdit}
              editedProfile={editedProfile}
              setEditedProfile={setEditedProfile}
              startEditProfile={startEditProfile}
              saveProfile={saveProfile}
              setProfileEdit={setProfileEdit}
              toggleEditInterest={toggleEditInterest}
            />
          )}
        </MainApp>
      )}
      
      {/* Chat Window */}
      {showChat && chatUser && currentUser && (
        <ChatWindow
          currentUser={currentUser}
          chatUser={chatUser}
          onClose={handleCloseChat}
        />
      )}
    </div>
  );
};

export default SwitzerlandMeetupApp;